import { useCallback, useEffect, useRef } from 'react'

/**
 * Aggregates a tray's independent dropdowns (pickers, Sort, hover previews…) into one "is
 * anything of mine open" signal for `onAnyDropdownOpenChange`, without re-rendering on every
 * keystroke inside them. A host panel (e.g. the Escape-key priority chain in
 * `right-panel/root-panel/hooks/use-escape-closes-panel.ts`) uses it to leave a dropdown-close
 * to Radix's own Escape handling.
 *
 * Returns a factory: `report('sort')` is the `onOpenChange` for that dropdown. Each call makes a
 * new function, so memoize the result when a consumer's effect depends on its identity.
 *
 * Panel unmount (e.g. the right panel closes/switches away) reports `false`, so no stale "open"
 * signal is left behind for whoever tracks it.
 */
export function useAnyDropdownOpen<K extends string>(
  initialOpen: Record<K, boolean>,
  onAnyDropdownOpenChange: ((open: boolean) => void) | undefined
): (key: K) => (open: boolean) => void {
  const openDropdowns = useRef(initialOpen)

  const reportAnyDropdownOpenChange = useCallback(
    (key: K) => (open: boolean) => {
      openDropdowns.current[key] = open
      const anyOpen = Object.values(openDropdowns.current).some(Boolean)

      onAnyDropdownOpenChange?.(anyOpen)
    },
    [onAnyDropdownOpenChange]
  )

  useEffect(() => {
    return () => onAnyDropdownOpenChange?.(false)
  }, [onAnyDropdownOpenChange])

  return reportAnyDropdownOpenChange
}
